















































































import { Component, Vue } from "vue-property-decorator";
import { firestore } from "firebase";
import router from "@/router/router";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { ComponentName } from "@/utils/RouteGuards";
import { getActiveUserId } from "@/data/repository/UserRepository";
import { getClientId } from "@/data/repository/ClientRepository";
import {
  renoReviewerKey,
  RenoReviewSuppression,
} from "@/data/models/RenoReviewEntry";
import {
  loadSuppressions,
  removeSuppression,
  SUPPRESSION_PAGE_SIZE,
} from "@/data/repository/RenoReviewRepository";
import { COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { themed } from "@/utils/Theme";
import {
  formatDateTohDateMonthYearString,
  formatDateToHoursAndMinuteString,
} from "@/utils/DateHelper";
import ToolbarOverflowItemComponent from "@/components/common/ToolbarOverflowItem.vue";
import { adminPortalChangeAccess } from "@/permissions/UserPermissions";
import { getReactiveActiveUser } from "@/data/repository/UserRepository";

/**
 * Dryings this reviewer has hidden, and the way to put them back.
 *
 * Suppression is otherwise irreversible from the UI: the queue subtracts whatever is in the
 * suppressions store and nothing else ever lists it. Deleting the row is the entire un-hide
 * mechanism — the drying reappears on the next pass.
 *
 * Only the reviewer's own hides, because suppression is per person (D9): one reviewer hiding a drying
 * has no effect on anyone else's queue.
 */
@Component({
  components: {
    "toolbar-overflow-item": ToolbarOverflowItemComponent,
  },
})
export default class RenoReviewHiddenComponent extends Vue {
  suppressions: RenoReviewSuppression[] = [];

  isLoading = true;
  isLoadingMore = false;
  loadError = false;

  /** Row currently being restored, so only that button spins. Empty when idle. */
  restoringKey = "";

  /** False once a short page comes back, since that means there is nothing after it. */
  canLoadMore = false;

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  /** The shared IRES admin login sees the admin portal read-only (#2610071). */
  get mayEdit(): boolean {
    return adminPortalChangeAccess(getReactiveActiveUser());
  }

  get reviewerKey(): string {
    return renoReviewerKey(getClientId(), getActiveUserId());
  }

  /** Just the figure, so the heading beside it stays put while loading. */
  get countValue(): string {
    if (this.isLoading) {
      return this.$t("admin.renoReview.countLoading").toString();
    }

    return this.suppressions.length === 0
      ? this.$t("admin.renoReview.countNone").toString()
      : String(this.suppressions.length);
  }

  /** The version-independent identity of a hidden drying, and what the un-hide deletes. */
  rowKey(suppression: RenoReviewSuppression): string {
    return `${suppression.serialNo}__${suppression.chainId}`;
  }

  dryingLabel(suppression: RenoReviewSuppression): string {
    return suppression.chainNumber
      ? this.$t("panelDetail.dryingProgressGraphControl.formatDrying", [
          suppression.chainNumber,
        ]).toString()
      : "—";
  }

  /**
   * Date and time together; the repo has no combined helper, so the two are composed here.
   *
   * Tolerates a missing timestamp: `chainNumber` and `chainStart` were added to the suppression after
   * the store existed, so a row written by an older build has neither.
   */
  formatMoment(timestamp: firestore.Timestamp | null | undefined): string {
    if (!timestamp) {
      return "—";
    }

    const date = timestamp.toDate();

    return `${formatDateTohDateMonthYearString(date)} ${formatDateToHoursAndMinuteString(date)}`;
  }

  private async load(startAfter?: firestore.Timestamp | null) {
    try {
      const page = await loadSuppressions(this.reviewerKey, startAfter);

      this.suppressions = startAfter ? [...this.suppressions, ...page] : page;
      // A full page might have more behind it; a short one cannot.
      this.canLoadMore = page.length === SUPPRESSION_PAGE_SIZE;
    } catch {
      this.loadError = true;
    }
  }

  async onLoadMore() {
    const last = this.suppressions[this.suppressions.length - 1];

    if (!last) {
      return;
    }

    this.isLoadingMore = true;
    await this.load(last.suppressedAt);
    this.isLoadingMore = false;
  }

  /**
   * Puts one drying back into the queue.
   *
   * The row is dropped from the list only after the delete succeeds, so a failed restore does not
   * leave the screen claiming something it did not do.
   */
  async onRestore(suppression: RenoReviewSuppression) {
    this.restoringKey = this.rowKey(suppression);

    try {
      await removeSuppression(
        this.reviewerKey,
        suppression.serialNo,
        suppression.chainId
      );

      this.suppressions = this.suppressions.filter(
        (candidate) => this.rowKey(candidate) !== this.rowKey(suppression)
      );
      SnackbarModule.showSuccess({ key: "admin.renoReview.hidden.restored" });
    } catch {
      SnackbarModule.showError({ key: "admin.renoReview.hidden.restoreFailed" });
    } finally {
      this.restoringKey = "";
    }
  }

  onBackToQueue() {
    router.push({ name: ComponentName.RENO_REVIEW_QUEUE });
  }

  // Access (portal client + RATE_DRYINGS) is enforced in router.beforeEach, which can await the
  // asynchronous role-settings load.
  async mounted() {
    ToolbarContentModule.setI18nResource("admin.renoReview.hidden.title");

    await this.load();
    this.isLoading = false;
  }
}
